import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLogSheet } from "../../contexts/LogSheetContext";
import { useCurrentTrips } from "../../hooks/useCurrentTrips";
import { useTrips } from "../../hooks/useTrips";
import { useSegments } from "../../hooks/useSegments";
import { useInteractions } from "../../hooks/useInteractions";
import { findDefaultSegmentId } from "../../utils/activityLogging";
import { findCurrentSegmentId, lastLoggedSegmentId, segmentLogSeeds } from "../../utils/tripDisplay";
import { LogActivitySheet } from "../interactions/LogActivitySheet";
import { LogActivityPicker } from "../interactions/LogActivityPicker";

/**
 * Host for the global Log sheet opened by the bottom-nav center button.
 * Logs against the trip you're currently viewing (any /trips/:tripId route),
 * falling back to your focused current trip. Only when there's neither does it
 * route to Trips so you can pick one. Feeds the existing LogActivityPicker the
 * same props the per-trip cards do.
 */
export function GlobalLogSheet() {
  const { open, closeLogSheet } = useLogSheet();
  const { focusedTrip } = useCurrentTrips();
  const location = useLocation();

  // The sheet lives in the persistent Layout, so it won't unmount on its own
  // when picking a place navigates to the new interaction. Close it whenever
  // the route changes while it's open (the background page swaps underneath).
  const openedPathRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      openedPathRef.current = null;
      return;
    }
    if (openedPathRef.current === null) {
      openedPathRef.current = location.pathname;
    } else if (openedPathRef.current !== location.pathname) {
      closeLogSheet();
    }
  }, [open, location.pathname, closeLogSheet]);

  if (!open) return null;

  // Prefer the trip in the URL (when viewing a specific trip), else the
  // focused current trip. "new" isn't a real trip id.
  const match = location.pathname.match(/^\/trips\/([^/]+)/);
  const routeTripId = match && match[1] !== "new" ? match[1]! : null;
  const tripId = routeTripId ?? focusedTrip?.id ?? null;

  // When viewing a specific segment, default the sheet to that segment (so the
  // bottom-nav Log matches the segment's own log button).
  const segMatch = location.pathname.match(/^\/trips\/[^/]+\/segments\/([^/]+)/);
  const routeSegmentId = segMatch && segMatch[1] !== "new" ? segMatch[1]! : null;

  if (!tripId) return <NoTripRedirect onClose={closeLogSheet} />;

  return (
    <GlobalLogSheetInner tripId={tripId} preferredSegmentId={routeSegmentId} onClose={closeLogSheet} />
  );
}

function NoTripRedirect({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  useEffect(() => {
    onClose();
    navigate("/trips");
  }, [navigate, onClose]);
  return null;
}

function GlobalLogSheetInner({
  tripId,
  preferredSegmentId,
  onClose,
}: {
  tripId: string;
  preferredSegmentId: string | null;
  onClose: () => void;
}) {
  const { trips } = useTrips();
  const { segments } = useSegments(tripId);
  const { interactions } = useInteractions(tripId);

  const tripName = useMemo(
    () => trips.find((t) => t.id === tripId)?.name ?? null,
    [trips, tripId],
  );

  // Default to the URL segment (viewing one), else the current (active)
  // segment (matches the "NOW" tag), else the last activity's segment.
  const defaultSegmentId = useMemo(() => {
    if (preferredSegmentId && segments.some((s) => s.id === preferredSegmentId)) {
      return preferredSegmentId;
    }
    return (
      findCurrentSegmentId(segments, lastLoggedSegmentId(interactions)) ??
      findDefaultSegmentId(segments, interactions)
    );
  }, [preferredSegmentId, segments, interactions]);

  // Seed "At" scoped to the default segment: recent activities under it, then
  // the segment's own entity chain (so the segment's park/ship shows too).
  const seedEntityIds = useMemo(
    () => segmentLogSeeds(segments, interactions, defaultSegmentId),
    [segments, interactions, defaultSegmentId],
  );

  return (
    <LogActivitySheet
      open
      onClose={onClose}
      title={tripName ? `Log activity · ${tripName}` : "Log activity"}
    >
      <LogActivityPicker
        tripId={tripId}
        segments={segments}
        defaultSegmentId={defaultSegmentId}
        seedEntityIds={seedEntityIds}
        interactions={interactions}
      />
    </LogActivitySheet>
  );
}
